import React from "react";
import { Helmet } from "react-helmet-async";
import MainLayout from "../../layouts/MainLayout";

const Legislations = () => {
  const featureCards = [
    {
      title: "Consolidated Legislation Access",
      description:
        "Browse Sri Lankan statutes from a single place without switching across scattered sources.",
    },
    {
      title: "Title and Year Search",
      description:
        "Quickly locate acts by title or publication period when you already know your target law.",
    },
    {
      title: "Full-Text Exploration",
      description:
        "Run full-text queries across legislation bodies to identify relevant clauses and provisions faster.",
    },
    {
      title: "Structured Reading Experience",
      description:
        "Move through acts and sections in a cleaner interface designed for day-to-day legal research.",
    },
  ];

  const workflow = [
    {
      title: "1. Search",
      description:
        "Find a statute by title, year, or specific full-text terms.",
    },
    {
      title: "2. Navigate",
      description:
        "Open the act and move directly to the sections you need.",
    },
    {
      title: "3. Cross-check",
      description:
        "Use the results to validate legal references in drafting and advice.",
    },
  ];

  return (
    <MainLayout>
      <Helmet>
        <title>Legislations | paralegal.lk</title>
        <meta
          name="description"
          content="Explore Sri Lankan consolidated legislation with title and full-text search on lex.paralegal.lk."
        />
        <link rel="canonical" href="https://www.paralegal.lk/legislations" />
      </Helmet>

      <div className="flex flex-col justify-center mx-auto max-w-6xl mt-50 p-20 text-pl-text-primary">
        <h1 className="mb-6 font-display font-semibold text-18 md:text-22">
          Legislations
        </h1>
        <p className="leading-relaxed text-pl-text-secondary mb-8">
          <span className="font-medium text-pl-text-primary">
            lex.paralegal.lk
          </span>{" "}
          is our consolidated legislation library built to make Sri Lankan
          statutory research faster, clearer, and easier to navigate.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="rounded-xl border border-pl-border p-6 md:col-span-2 hover:shadow-lg transition-shadow">
            <h2 className="mb-3 font-bold text-16 md:text-18 text-pl-accent">
              Built for Practical Legal Research
            </h2>
            <p className="leading-relaxed mb-3">
              Whether you are preparing submissions, reviewing compliance, or
              validating statutory references, lex.paralegal.lk helps you locate
              relevant legislation quickly and reliably.
            </p>
            <p className="leading-relaxed text-pl-text-secondary">
              The experience is designed for professionals who need immediate
              access to provisions, without friction.
            </p>
          </div>

          {featureCards.map((card) => (
            <div
              key={card.title}
              className="rounded-xl border border-pl-border p-6 hover:shadow-lg transition-shadow"
            >
              <h3 className="mb-3 font-bold text-16 md:text-18 text-pl-accent">
                {card.title}
              </h3>
              <p className="leading-relaxed text-pl-text-secondary">
                {card.description}
              </p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-pl-border p-6 mb-8 hover:shadow-lg transition-shadow">
          <h2 className="mb-4 font-bold text-16 md:text-18 text-pl-accent">
            Research Flow
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {workflow.map((step) => (
              <div
                key={step.title}
                className="rounded-lg border border-pl-border bg-pl-bg-elevated p-4"
              >
                <h3 className="font-semibold mb-2">{step.title}</h3>
                <p className="text-pl-text-secondary leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-pl-border p-6 hover:shadow-lg transition-shadow">
          <h2 className="mb-3 font-bold text-16 md:text-18 text-pl-accent">
            Start Exploring Legislation
          </h2>
          <p className="leading-relaxed text-pl-text-secondary mb-5">
            Open lex.paralegal.lk to search by title or switch to full-text
            mode when you need deeper statutory coverage.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://lex.paralegal.lk"
              className="inline-flex items-center justify-center px-6 py-3 rounded-pl bg-pl-accent text-white hover:bg-pl-accent-hover transition-colors duration-150"
            >
              Try Legislations
            </a>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Legislations;
